/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

export const setUtilQueueHandlers = handlers => {
  queueHandlers.trackEvent = handlers.trackEvent;
  queueHandlers.pageEvent = handlers.pageEvent;
};

const queueHandlers = {
  trackEvent: (
    _name: string,
    _defaultProps: Record<string, string | number | boolean>,
    _props: Record<string, string | number | boolean>,
    _customProps: Record<string, string | number | boolean>,
    _groupProps: Record<string, string | number | boolean>
  ): void => null,
  pageEvent: (
    _name: string,
    _defaultProps: Record<string, string | number | boolean>,
    _props: Record<string, string | number | boolean>,
    _customProps: Record<string, string | number | boolean>,
    _groupProps: Record<string, string | number | boolean>
  ): void => null
};

const eventRegEx = /^\s*(\w+):\s*"([^"]*)"(?:\s*(\w+))?(?:\s*{([^}]*)})?/;

// Converts a text defintion of events to an 'eventDefs' structure:
// Input: events: lines of the form
//    `eventName:  "Event Action"  object_type  {group props list}
//     ...`
// Output: eventDefs = {
//   eventName: eventDef,
//   ...
// }
// Where eventDef = {
//   action: 'Event Action',
//   type: 'object type',
//   groupProps: {
//     common: [true|false],
//     assitant: [true|false],
//     skill: [true|false]
//   }
// }
const parseEvents = (events: string) => {
  const eventDefs = {};
  events.split('\n').forEach(event => {
    const match = event.match(eventRegEx);
    if (match) {
      const key = match[1];
      const action = match[2];
      const type = match[3] ? match[3].replace('_', ' ') : null;
      const groups = match[4] || '';
      eventDefs[key] = {
        action,
        type,
        groupProps: {
          common: groups.includes('common'),
          assistant: groups.includes('assistant'),
          skill: groups.includes('skill'),
          step: groups.includes('step'),
          action: groups.includes('action'),
          environment: groups.includes('environment'),
          integration: groups.includes('integration'),
          customExtension: groups.includes('customExtension')
        }
      };
    }
  });
  return eventDefs;
};

/**
 * Converts an 'eventDefs' structure (from parsing above) into a matching structure
 * with a function assigned to each eventName.  The function is a reference to the
 * 'queueFn' parameter with its `this` value bound to the 'eventDef' details.
 * ```
 *   Output: eventFuncs = {
 *     eventName: queueFn() with 'this' set to 'eventDef' details
 *   }
 * ```
 *
 */
const buildEvents = (
  events: string,
  queueFn
): Record<string, (..._args: any) => void> => {
  const eventDefs = parseEvents(events);
  const eventFuncs = {};
  for (const key in eventDefs) {
    eventFuncs[key] = queueFn.bind(eventDefs[key]);
  }
  return eventFuncs;
};

const buildObjEvents = (events, name, actionType, defaultProps) => {
  // Queue function: when invoked 'this' will be set to the 'eventDef' details
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      action: this.action,
      actionType: actionType,
      objectType: this.type,
      ...userProps
    };
    queueHandlers.trackEvent(
      name,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

const buildCTAEvents = (events, name, defaultProps) => {
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      CTA: this.action,
      action: this.action,
      actionType: this.type,
      ...userProps
    };
    queueHandlers.trackEvent(
      name,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

const buildProcessEvents = (events, name, defaultProps) => {
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      action: this.action,
      processType: this.type,
      ...userProps
    };
    queueHandlers.trackEvent(
      name,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

const buildExperimentEvents = (events, name, defaultProps) => {
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      name: this.action,
      ...userProps
    };
    queueHandlers.trackEvent(
      name,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

const buildModalEvents = (events, name, defaultProps) => {
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      name: this.action,
      ...userProps
    };
    queueHandlers.trackEvent(
      name,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

const buildVideoEvents = (events, name, defaultProps) => {
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      name: this.action,
      action: this.action,
      ...userProps
    };
    queueHandlers.trackEvent(
      name,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

const buildPageEvents = (events, defaultProps) => {
  const invokeQueue = function (userProps, customProps) {
    const props = {
      ...defaultProps,
      name: this.action,
      ...userProps
    };
    queueHandlers.pageEvent(
      this.action,
      defaultProps,
      props,
      customProps,
      this.groupProps
    );
  };
  return buildEvents(events, invokeQueue);
};

export const defineCreatedEvents = (defaultProps, events) => {
  return buildObjEvents(events, 'Created Object', 'Created', defaultProps);
};

export const defineDeletedEvents = (defaultProps, events) => {
  return buildObjEvents(events, 'Deleted Object', 'Deleted', defaultProps);
};

export const defineUpdatedEvents = (defaultProps, events) => {
  return buildObjEvents(events, 'Updated Object', 'Updated', defaultProps);
};

export const defineSearchedEvents = (defaultProps, events) => {
  return buildObjEvents(events, 'Searched Objects', 'Searched', defaultProps);
};

export const defineCTAEvents = (defaultProps, events) => {
  return buildCTAEvents(events, 'CTA Clicked', defaultProps);
};

export const defineRanProcessEvents = (defaultProps, events) => {
  return buildProcessEvents(events, 'Ran Process', defaultProps);
};

export const defineExperimentEvents = (defaultProps, events) => {
  return buildExperimentEvents(events, 'Experiment Viewed', defaultProps);
};

export const defineModalEvents = (defaultProps, events) => {
  return buildModalEvents(events, 'Viewed Modal Dialog', defaultProps);
};

export const definePageEvents = (defaultProps, events) => {
  return buildPageEvents(events, defaultProps);
};

export const defineVideoEvents = (defaultProps, events) => {
  return buildVideoEvents(events, 'Video Playback Started', defaultProps);
};
